// SPDX-License-Identifier: AGPL-3.0-or-later

import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {
	FEED_IMAGE_HARD_MAX_BYTES,
	FEED_VIDEO_HARD_MAX_BYTES,
	FEED_VIDEO_HARD_MAX_DURATION_SECONDS,
} from '@fluxer/constants/src/LimitConstants';
import {FEED_VIDEO_CONTENT_TYPES} from '@fluxer/schema/src/domains/feed/FeedRequestSchemas';

export const FEED_MAX_IMAGES = 4;
export const FEED_IMAGE_TYPES: ReadonlySet<string> = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
export const FEED_IMAGE_ACCEPT = [...FEED_IMAGE_TYPES].join(',');
// Micup akis-v3: before any request only the hard caps no account goes past; the account's own limit (10 MiB, more for
// content creators) comes back from the server as FILE_SIZE_TOO_LARGE with max_size before the image is sent. The
// feed plans every image as one presigned PUT, which holds 50 MiB.
export const FEED_MAX_IMAGE_BYTES = FEED_IMAGE_HARD_MAX_BYTES;

export type FeedImageRejection = 'type' | 'count' | 'size';

// One video per post, alone (no images, no poll), uploaded to Bunny Stream with TUS (POST /feed/videos). The server's
// four types, and before any request the hard caps (4 GiB, 3 hours); the start answers with the account's own size
// and length (500 MiB and 10 minutes by default) before anything is uploaded. The picker also names the extensions a
// system may know no type for, so those files are not hidden from it.
export const FEED_VIDEO_TYPES: ReadonlySet<string> = new Set(FEED_VIDEO_CONTENT_TYPES);
export const FEED_VIDEO_ACCEPT = [...FEED_VIDEO_TYPES, '.mov', '.mkv'].join(',');
export const FEED_MAX_VIDEO_BYTES = FEED_VIDEO_HARD_MAX_BYTES;
export const FEED_MAX_VIDEO_SECONDS = FEED_VIDEO_HARD_MAX_DURATION_SECONDS;
// Poster images: still ones only (the server refuses an animated GIF).
export const FEED_POSTER_ACCEPT = 'image/png,image/jpeg,image/webp';
const FEED_VIDEO_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
	mp4: 'video/mp4',
	webm: 'video/webm',
	mov: 'video/quicktime',
	mkv: 'video/x-matroska',
};

export type FeedVideoRejection = 'type' | 'size' | 'length';

// Single choice; the server checks the same bounds and refuses repeated options.
export const FEED_POLL_MIN_OPTIONS = 2;
export const FEED_POLL_MAX_OPTIONS = 4;
export const FEED_POLL_OPTION_MAX_LENGTH = 80;
export const FEED_POLL_DURATIONS_HOURS = [1, 24, 72, 168] as const;

export function isFeedEnabled(): boolean {
	return LimitResolver.resolve({key: 'feature_feed', fallback: 0}) > 0;
}

// Off: no tabs, the page shows only the posts of the accounts you follow (GET /feed/discover answers 403).
export function isFeedDiscoverEnabled(): boolean {
	return LimitResolver.resolve({key: 'feature_feed_discover', fallback: 0}) > 0;
}

// Micup akis-siralama: off by default; Discover has no Popular / Latest switch (GET /feed/discover/new answers 403).
export function isFeedNewEnabled(): boolean {
	return LimitResolver.resolve({key: 'feature_feed_new', fallback: 0}) > 0;
}

// Off: no suggestions beside the feed and no request (GET /feed/suggestions answers 403).
export function isFeedSuggestionsEnabled(): boolean {
	return LimitResolver.resolve({key: 'feature_feed_suggestions', fallback: 0}) > 0;
}

// Keşfet's image arm: off, a stranger's Discover post shows no media, and its comment GIFs stay links too.
export function isFeedDiscoverMediaEnabled(): boolean {
	return LimitResolver.resolve({key: 'feature_feed_discover_media', fallback: 0}) > 0;
}

// Off by default: the composer shows no video button (POST /feed/videos answers 403).
export function isFeedVideoEnabled(): boolean {
	return LimitResolver.resolve({key: 'feature_feed_video', fallback: 0}) > 0;
}

// Off by default: no schedule button and no list of planned posts (the /feed/scheduled-posts routes answer 403).
export function isFeedScheduledEnabled(): boolean {
	return LimitResolver.resolve({key: 'feature_feed_scheduled', fallback: 0}) > 0;
}

// The server takes a planned time from 5 minutes to 30 days ahead, rounded down to the minute.
export const FEED_SCHEDULE_MIN_LEAD_MS = 5 * 60_000;
export const FEED_SCHEDULE_MAX_LEAD_MS = 30 * 24 * 60 * 60_000;

export function isFeedScheduleTimeValid(at: Date, now = Date.now()): boolean {
	const lead = at.getTime() - now;
	return lead >= FEED_SCHEDULE_MIN_LEAD_MS && lead <= FEED_SCHEDULE_MAX_LEAD_MS;
}

// Micup akis-kategori: off by default; no topics, no category picker and no request (GET /feed/categories answers 403).
export function isFeedCategoriesEnabled(): boolean {
	return LimitResolver.resolve({key: 'feature_feed_categories', fallback: 0}) > 0;
}

// Micup akis-gundem: off by default; no trending strip, no topic in the post box and no request (GET /feed/topics
// answers 403).
export function isFeedTopicsEnabled(): boolean {
	return LimitResolver.resolve({key: 'feature_feed_topics', fallback: 0}) > 0;
}

// Browsers give no type for a file the system has none for (often .mkv on Windows); the server needs one of its four.
// The copy only points at the picked file's bytes.
export function feedVideoFile(file: File): File {
	if (file.type) return file;
	const type = FEED_VIDEO_TYPE_BY_EXTENSION[file.name.split('.').pop()?.toLowerCase() ?? ''];
	return type ? new File([file], file.name, {type, lastModified: file.lastModified}) : file;
}

// `durationSeconds`: what the browser read from the file, null when it could not.
export function validateFeedVideo(
	file: File,
	maxBytes: number,
	durationSeconds: number | null = null,
	maxSeconds: number = FEED_MAX_VIDEO_SECONDS,
): FeedVideoRejection | null {
	if (!FEED_VIDEO_TYPES.has(file.type)) return 'type';
	if (file.size > maxBytes) return 'size';
	if (durationSeconds !== null && durationSeconds > maxSeconds) return 'length';
	return null;
}

// Keeps the added files that fit a post and names the first reason one was left out. The server checks again; this
// only spares a request that would fail.
export function validateFeedImages(
	current: ReadonlyArray<File>,
	added: ReadonlyArray<File>,
	maxBytes: number,
): {accepted: Array<File>; rejection: FeedImageRejection | null} {
	const accepted: Array<File> = [];
	let rejection: FeedImageRejection | null = null;
	for (const file of added) {
		if (!FEED_IMAGE_TYPES.has(file.type)) {
			rejection ??= 'type';
		} else if (file.size > maxBytes) {
			rejection ??= 'size';
		} else if (current.length + accepted.length >= FEED_MAX_IMAGES) {
			rejection ??= 'count';
		} else {
			accepted.push(file);
		}
	}
	return {accepted, rejection};
}
